/**
 * @file SenderPointSection.tsx
 * @description Пункт сдачи посылок в настройках перевозчика (phase19.12).
 *
 * Раньше здесь было голое поле «Код пункта сдачи» — по коду невозможно
 * проверить, тот ли это пункт. Теперь показываем карточку выбранного пункта
 * (адрес, график, телефон) и два способа сменить его: списком или на карте.
 */

import { List, Loader2, Map as MapIcon, MapPin } from "lucide-react";
import { useState } from "react";

import { PickupPointMapDialog } from "./PickupPointMapDialog";
import { usePickupPoint } from "./usePickupPoint";
import { SdekPickupPointPicker } from "../orders/SdekPickupPointPicker";
import { Button } from "../ui/button";
import type { CarrierKind } from "../../lib/api-shop-carriers";

interface Props {
  carrier: CarrierKind;
  /** Код пункта сдачи из настроек (пусто — пункт не выбран). */
  code: string;
  /** Город отправления — с него открываем поиск и карту. */
  city?: string;
  /** Ключ Яндекс.Карт из настроек перевозчика. */
  mapApiKey?: string | null;
  disabled?: boolean;
  onChange: (code: string) => void;
}

export function SenderPointSection({
  carrier,
  code,
  city,
  mapApiKey,
  disabled,
  onChange,
}: Props) {
  const [listOpen, setListOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const { point, loading } = usePickupPoint(code, city);

  return (
    <section className="flex flex-col gap-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
        Пункт сдачи
      </div>

      {code ? (
        <div className="flex flex-col gap-1 rounded-lg p-3 [box-shadow:inset_0_0_0_1px_var(--outline-variant)]">
          <div className="flex items-start gap-2">
            <MapPin className="mt-0.5 size-4 shrink-0 text-on-surface-variant" />
            <div className="min-w-0">
              <div className="font-mono text-sm font-medium uppercase">{code}</div>
              {loading ? (
                <div className="flex items-center gap-1 text-xs text-on-surface-variant">
                  <Loader2 className="size-3 animate-spin" /> Ищем пункт в справочнике…
                </div>
              ) : point ? (
                <>
                  <div className="text-sm">{point.address}</div>
                  {point.workingHours ? (
                    <div className="text-xs text-on-surface-variant">
                      {point.workingHours}
                    </div>
                  ) : null}
                  {point.phone ? (
                    <a
                      href={`tel:${point.phone}`}
                      className="text-xs text-primary hover:underline"
                    >
                      {point.phone}
                    </a>
                  ) : null}
                </>
              ) : (
                <div className="text-xs text-on-surface-variant">
                  Пункта с таким кодом нет в кэше — обновите кэш ПВЗ или выберите другой.
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-on-surface-variant">Пункт сдачи не выбран.</p>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <span className="text-xs text-on-surface-variant">
          {code ? "Выбрать другой:" : "Выбрать пункт:"}
        </span>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            size="sm"
            className="min-h-11"
            disabled={disabled}
            onClick={() => setListOpen(true)}
          >
            <List className="mr-1 size-4" />
            Списком
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="min-h-11"
            disabled={disabled}
            onClick={() => setMapOpen(true)}
          >
            <MapIcon className="mr-1 size-4" />
            На карте
          </Button>
        </div>
      </div>

      <SdekPickupPointPicker
        open={listOpen}
        onClose={() => setListOpen(false)}
        onPick={(p) => onChange(p.externalCode)}
      />
      <PickupPointMapDialog
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        carrier={carrier}
        apiKey={mapApiKey ?? null}
        initialCity={city}
        onPick={(p) => onChange(p.externalCode)}
      />
    </section>
  );
}
